// src/features/records/services/attachments/state.js

import { db } from '../../../../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

// Version-aware attachment cache - Map<recordId, { version: number; data: Attachment[] }>
const attachmentCache = new Map();

// Active attachment subscriptions - Map<recordId, unsubscribe()>
const attachmentSubscriptions = new Map();

// Track active records for attachment fetching
const activeRecords = new Set();

export function getAttachmentCacheEntry(recordId) {
  return attachmentCache.get(recordId);
}

export function setAttachmentCacheEntry(recordId, entry) {
  attachmentCache.set(recordId, entry);
}

export function deleteAttachmentCacheEntry(recordId) {
  attachmentCache.delete(recordId);
}

/**
 * Start a real-time listener for a record's attachments.
 */
export async function startAttachmentListener(recordId, expectedVersion) {
  stopRecordAttachmentListener(recordId);

  const attachmentsRef = collection(db, 'records', recordId, 'attachments');
  const q = query(attachmentsRef, orderBy('uploadedAt', 'desc'));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const attachments = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        attachments.push({
          id: doc.id,
          ...data,
        });
      });

      setAttachmentCacheEntry(recordId, {
        version: expectedVersion,
        data: attachments,
      });
    },
    (error) => {
      console.error('Error in attachment listener:', error);
    }
  );

  attachmentSubscriptions.set(recordId, unsubscribe);

  const cached = getAttachmentCacheEntry(recordId);
  return cached?.data || [];
}

/**
 * Stop real-time listener for a record's attachments.
 */
export function stopRecordAttachmentListener(recordId) {
  const unsubscribe = attachmentSubscriptions.get(recordId);
  if (unsubscribe) {
    unsubscribe();
    attachmentSubscriptions.delete(recordId);
  }
}

/**
 * Mark a record as active (for attachment fetching).
 */
export function setRecordActive(recordId, isActive) {
  if (isActive) {
    activeRecords.add(recordId);
  } else {
    activeRecords.delete(recordId);
    stopRecordAttachmentListener(recordId);
  }
}

/**
 * Check if a record is active.
 */
export function isRecordActive(recordId) {
  return activeRecords.has(recordId);
}

/**
 * Clear attachment cache for a specific record or all records.
 */
export function clearAttachmentCache(recordId = null) {
  if (recordId) {
    deleteAttachmentCacheEntry(recordId);
    stopRecordAttachmentListener(recordId);
  } else {
    attachmentCache.clear();
    attachmentSubscriptions.forEach((unsubscribe) => unsubscribe());
    attachmentSubscriptions.clear();
    activeRecords.clear();
  }
}
